import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector, HostListener, Input, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { addDays, addWeeks, endOfToday, startOfToday, startOfWeek } from 'date-fns';
import { fi } from 'date-fns/locale';
import { combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { ConfirmDialogComponent } from 'src/app/confirm-dialog/confirm-dialog.dialog';
import { Room } from 'src/app/models/room.model';
import { SiteProjectPeriod } from 'src/app/models/site-project-period.model';
import { SiteProject } from 'src/app/models/site-project.model';
import { Site } from 'src/app/models/site.model';
import { SubItem } from 'src/app/models/sub-item.model';
import { AreasService } from 'src/app/services/areas.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { SiteProjectPeriodsService } from 'src/app/services/site-project-period.service';
import { SiteProjectsService } from 'src/app/services/site-projects.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { ProjectJobDialogComponent } from 'src/app/site-project/job-dialog/job.dialog';
import { TextDialogComponent } from 'src/app/text-dialog/text-dialog.dialog';
import { defaultProjectName } from 'src/app/utility/kanban';
import { memo } from 'src/app/utility/memo';
import { Roles } from 'src/app/utility/role';
import { convertTimestamp, numberOfWeek } from 'src/app/utility/time';

const mobileWidth = 1024;

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
  ],
})
export class CalendarComponent {
  private areasService = inject(AreasService);
  private contractorsService = inject(ContractorsService);
  private injectorContext = inject(EnvironmentInjector);
  private modalCtrl = inject(ModalController);
  private navigationService = inject(NavigationService);
  private siteProjectPeriodsService = inject(SiteProjectPeriodsService);
  private siteProjectsService = inject(SiteProjectsService);
  private translate = inject(TranslateService);
  private usersService = inject(UserSessionService);

  private mobileView = signal(false);
  private selectedDayS = signal<Date>(startOfWeek(startOfToday(), { weekStartsOn: 1 }));
  site = input<Site>();

  skipHeader = input(false);
  showEmpty = input(false);
  showSite = input(false);
  showNew = input(false);

  @Input()
  set selectedDay(value: Date) {
    if (value != null) {
      this.selectedDayS.set(value);
    }
  }

  @Input()
  set viewSegmentValue(value: 'day' | 'week') {
    if (value) {
      this.viewSegment.set(value);
    }
  }

  loading = signal(true);
  weekNumber = computed(() => numberOfWeek(this.selectedDayS()));
  viewSegment = signal<string>('day');
  selectedContracor = signal<SubItem>(null);
  selectedArea = signal<Room>(null);
  role = this.usersService.role;

  siteProjects$ = toObservable(this.site, { injector: this.injectorContext }).pipe(
    filter((site) => site != null),
    switchMap((site) => combineLatest([this.siteProjectsService.getList({ site: site.guid, hideEnded: true })])),
    map(([projects]) => projects.filter((it) => it.site === this.site()?.guid && it.name !== defaultProjectName)),
    map((projects) => projects.sort(fieldSorter(['name']))),
    map((items) => items.map((it) => {
      const periods$ = this.siteProjectPeriodsService.getList({ project: it.guid }).pipe(
        map((periods) => periods.map((p) => {
          const areaNames = p.areas?.map((a) => a.name).join(',');
          const contractor$ = this.contractorsService.get(p.contractor?.guid);
          return Object.assign({}, p, { areaNames, contractor$ });
        })),
        map((periods) => periods.sort(fieldSorter(['order']))),
        switchMap((periods) => combineLatest([
          toObservable(this.selectedContracor, { injector: this.injectorContext }), toObservable(this.selectedArea, { injector: this.injectorContext }), of(periods),
        ])),
        map(([contractor, area, periods]) => periods
          .filter((p) => contractor == null || p.contractor?.guid === contractor.guid)
          .filter((p) => area == null || p.areas?.some((a) => a.guid === area.siteArea.root && a.id === area.siteArea.id)),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
      return Object.assign({}, it, { periods$ });
    })),
    tap(() => this.loading.set(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  days = computed(() => {
    return this.getDays(this.mobileView(), this.selectedDayS());
  });

  weeks = computed(() => {
    return this.getWeeks(this.mobileView(), this.selectedDayS());
  });

  viewSegmentButtons = [
    {
      value: 'day',
      name: 'workMachines.day',
      show: true,
    },
    {
      value: 'week',
      name: 'workMachines.week',
      show: true,
    },
  ];

  getPeriodValue = memo((phase: SiteProjectPeriod, day: Date) => this._getPeriodValue(phase, day));

  private getDays(mobileView: boolean, selectedDay: Date) {
    const days: { date: Date; dateString: string; isToday: boolean; isWeekend: boolean }[] = [];

    const daysBack = 0;
    const weekAgo = addDays(selectedDay, daysBack);

    const maxDate = mobileView ? addDays(selectedDay, 4) : addWeeks(selectedDay, 2);

    let currentDate = weekAgo;
    while (currentDate <= maxDate) {
      const item = {
        date: currentDate,
        dateString: currentDate.toLocaleDateString('fi'),
        isToday: currentDate.toISOString() === startOfToday().toISOString(),
        isWeekend: currentDate.getDay() === 0 || currentDate.getDay() === 6,
      };
      days.push(item);
      currentDate = addDays(currentDate, 1);
    }

    return days;
  }

  private getWeeks(mobileView: boolean, selectedDay: Date) {
    const weeks: { date: Date; dateString: string; isToday: boolean; isWeekend: boolean }[] = [];

    const start = startOfWeek(selectedDay, { locale: fi });
    const currentWeekNumber = numberOfWeek(endOfToday());

    const maxDate = mobileView ? addWeeks(selectedDay, 4) : addWeeks(selectedDay, 14);

    let currentDate = start;
    while (currentDate <= maxDate) {
      const weeknumber = numberOfWeek(currentDate);
      const item = {
        date: currentDate,
        dateString: weeknumber.toString(),
        isToday: currentWeekNumber === weeknumber,
        isWeekend: false,
      };
      weeks.push(item);
      currentDate = addWeeks(currentDate, 1);
    }

    return weeks;
  }

  private _getPeriodValue(phase: SiteProjectPeriod, day: Date): 'start' | 'end' | 'on' | 'off' {
    const start = convertTimestamp(phase.start);
    const end = convertTimestamp(phase.end);
    if (this.viewSegment() === 'day') {
      if (day > start && day < end) {
        return 'on';
      } else if (day.toLocaleDateString('fi') === start.toLocaleDateString('fi')) {
        return 'start';
      } else if (day.toLocaleDateString('fi') === end.toLocaleDateString('fi')) {
        return 'end';
      }
    } else {
      const dayWeeknumber = numberOfWeek(day);
      const startWeeknumber = numberOfWeek(start);
      const endWeeknumber = numberOfWeek(end);
      if (dayWeeknumber > startWeeknumber && dayWeeknumber < endWeeknumber) {
        return 'on';
      } else if (dayWeeknumber === startWeeknumber) {
        return 'start';
      } else if (dayWeeknumber === endWeeknumber) {
        return 'end';
      }
    }

    return 'off';
  }

  private openNewProjectModal() {
    this.navigationService.navigateToSiteProjectCreate(this.site()?.guid, 'workspace');
  }

  @HostListener('window:resize')
  onResize() {
    this.mobileView.set(window.innerWidth < mobileWidth);
  }

  viewSegmentChanged(value: string) {
    if (value) {
      this.viewSegment.set(value);
    }
  }

  selectContractor(item: SubItem) {
    if (item != null) {
      this.selectedContracor.set(item);
    } else {
      this.selectedContracor.set(null);
    }
  }

  selectArea(item: Room) {
    if (item != null) {
      this.selectedArea.set(item);
    } else {
      this.selectedArea.set(null);
    }
  }

  showAreas(period: SiteProjectPeriod) {
    this.modalCtrl.create({
      component: TextDialogComponent, componentProps: { text: period.areas.map((it) => it.name).join(',') },
    }).then(async (m) => m.present());
  }

  editPeriod(project: SiteProject, item: SiteProjectPeriod, site: Site) {
    const user = this.usersService.currentUserS();
    const canEdit = ([Roles.manager].includes(user.role) && (!user.rights?.officer || user.rights?.calendar)) || [Roles.admin, Roles.supervisor].includes(user.role);
    if (!canEdit) {
      return;
    }
    this.modalCtrl.create({
      component: ProjectJobDialogComponent,
      componentProps: { item, site, contractor: item.contractor, project },
      cssClass: ['workspace-modal', 'stack-modal', 'project-job-workspace-modal'],
    }).then((m) => {
      m.present();

      m.onDidDismiss<SiteProjectPeriod>().then((data) => {
        if (data.data) {
          this.siteProjectPeriodsService.update(data.data);
        }
      });
    });
  }

  newProject() {
    this.areasService.getSiteAreas(this.site()).pipe(take(1)).subscribe(async (areas) => {
      if (areas.length <= 1) {
        const confirmModal = await this.modalCtrl.create({
          component: ConfirmDialogComponent, componentProps: {
            title: this.translate.instant('room.missingRooms'),
            text: this.translate.instant('room.addRooms'),
            buttons: [this.translate.instant('general.cancel'), this.translate.instant('general.no'), this.translate.instant('general.yes')],
          },
          cssClass: ['stack-modal'],
        });
        confirmModal.present();
        const confirmData = await confirmModal.onDidDismiss();
        if (confirmData.data) {
          if (confirmData.data === 0) {
            return;
          } else if (confirmData.data === 1) { // Different project
            this.openNewProjectModal();
          } else {
            this.navigationService.navigateToSite(this.site()?.guid, { tabs: 'settings', subtabs: 'areas' });
          }
        }
      } else {
        this.openNewProjectModal();
      }
    });
  }

  onProject(site: string, project: string) {
    this.navigationService.navigateToSiteProject(site, project);
  }

  onSite(site: string) {
    this.navigationService.navigateToSite(site);
  }
}
